/** Stock–bond rolling correlation regimes and hedge-read copy. */

export const REGIME_THRESHOLD = 0.2;
export const TREND_DELTA = 0.15;

export const STOCK_BOND_INTRO =
  'Bonds hedge stocks when they move in opposite directions. When investors fear a slowdown, they sell stocks and buy safe government bonds, so bond prices rise while stocks fall. This cushions a portfolio. The rolling correlation shows whether that is happening. Below zero, bonds are working as a hedge. Above zero, bonds and stocks rise and fall together, usually because inflation or rising interest rates are hurting both. From 2016 to 2021 the hedge mostly worked. Since 2022 it has mostly failed.';

export const REGIME_ROWS = [
  {
    id: 'hedge',
    range: '≤ −0.20',
    label: 'Hedge working',
    emoji: '🟢',
    tooltip:
      'Bonds are moving against stocks. When stocks drop, bonds tend to rise and soften the loss.',
  },
  {
    id: 'mixed',
    range: '−0.20 to +0.20',
    label: 'Mixed / neutral',
    emoji: '🟡',
    tooltip:
      'No clear link right now. Bonds neither protect nor add to stock losses.',
  },
  {
    id: 'no_hedge',
    range: '≥ +0.20',
    label: 'No hedge',
    emoji: '🔴',
    tooltip:
      'Bonds and stocks are moving together, usually because of inflation or rate fears. Bonds won’t cushion a stock sell-off.',
  },
];

export function fmtSignedCorr(value) {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}`;
}

/** @param {number | null} c correlation in [-1, 1] */
export function regime(c) {
  if (c == null || Number.isNaN(c)) {
    return {
      id: 'unknown',
      label: '—',
      emoji: '',
      tooltip: 'Not enough data for this window.',
      cssClass: 'neutral',
    };
  }
  if (c <= -REGIME_THRESHOLD) {
    const row = REGIME_ROWS[0];
    return { id: row.id, label: row.label, emoji: row.emoji, tooltip: row.tooltip, cssClass: 'hedge' };
  }
  if (c >= REGIME_THRESHOLD) {
    const row = REGIME_ROWS[2];
    return { id: row.id, label: row.label, emoji: row.emoji, tooltip: row.tooltip, cssClass: 'no-hedge' };
  }
  const row = REGIME_ROWS[1];
  return { id: row.id, label: row.label, emoji: row.emoji, tooltip: row.tooltip, cssClass: 'mixed' };
}

export function trendLabel(c30, c90) {
  if (c30 == null || c90 == null) return 'unknown';
  if (c30 < c90 - TREND_DELTA) return 'improving';
  if (c30 > c90 + TREND_DELTA) return 'worsening';
  return 'stable';
}

export function generateCurrentTooltip(c30, c60, c90) {
  if (c30 == null && c60 == null && c90 == null) {
    return 'Need more daily history to compute rolling correlation.';
  }
  const r90 = regime(c90);
  const r30 = regime(c30);
  const trend = trendLabel(c30, c90);
  return (
    `${r90.emoji} ${r90.label} (90d ${fmtSignedCorr(c90)}) · short-term ${r30.emoji} ${r30.label} (30d ${fmtSignedCorr(c30)}) · trend ${trend}. `
    + 'Hedge confirmed when 60d and 90d stay below −0.20.'
  );
}

export function currentSituationSummary(c30, c60, c90) {
  const r30 = regime(c30);
  const r60 = regime(c60);
  const r90 = regime(c90);
  const trend = trendLabel(c30, c90);

  let headline;
  if (r90.id === 'hedge' && r60.id === 'hedge') {
    headline = 'Hedge working on 60d and 90d windows.';
  } else if (r90.id === 'no_hedge' && r60.id === 'no_hedge' && r30.id === 'no_hedge') {
    headline = 'No hedge — bonds and stocks moving together.';
  } else if (r30.id === 'hedge' && (r60.id !== 'hedge' || r90.id !== 'hedge')) {
    headline = 'Mixed, with the hedge possibly returning.';
  } else if (trend === 'improving' && (r60.id !== 'hedge' || r90.id !== 'hedge')) {
    headline = 'Mixed, with the hedge possibly returning.';
  } else if (r30.id === 'mixed' && r60.id === 'mixed' && r90.id === 'mixed') {
    headline = 'Mixed / neutral — no clear hedge link.';
  } else if (r90.id === 'no_hedge' || r60.id === 'no_hedge') {
    headline = 'Hedge weak — longer windows still positive or mixed.';
  } else {
    headline = 'Mixed regime across rolling windows.';
  }

  const parts = [
    `30d ${fmtSignedCorr(c30)} (${r30.label})`,
    `60d ${fmtSignedCorr(c60)} (${r60.label})`,
    `90d ${fmtSignedCorr(c90)} (${r90.label})`,
  ];

  let detail = `${parts.join('; ')}. `;

  if (trend === 'improving') {
    detail += 'Short-term correlation is lower than the 90d window — trend improving toward a hedge. ';
  } else if (trend === 'worsening') {
    detail += 'Short-term correlation is above the 90d window — link tightening, hedge weakening. ';
  }

  if (r90.id !== 'hedge' || r60.id !== 'hedge') {
    detail += 'Bonds are not yet a reliable hedge on longer windows. ';
  } else {
    detail += `${r90.tooltip} `;
  }

  detail += 'Confirmation: 60d and 90d both below −0.20.';

  return {
    headline,
    detail,
    tooltipLine: generateCurrentTooltip(c30, c60, c90),
    r30,
    r60,
    r90,
    trend,
    primaryCss: r90.cssClass !== 'neutral' ? r90.cssClass : r30.cssClass,
  };
}

export function corrsAtTime(corr30, corr60, corr90, time) {
  const pick = (bars) => bars?.find((b) => b.time === time)?.value ?? null;
  return {
    c30: pick(corr30),
    c60: pick(corr60),
    c90: pick(corr90),
  };
}

export function situationAtTime(corr30, corr60, corr90, time) {
  const { c30, c60, c90 } = corrsAtTime(corr30, corr60, corr90, time);
  if (c30 == null && c60 == null && c90 == null) return null;
  return {
    time,
    ...currentSituationSummary(c30, c60, c90),
    c30,
    c60,
    c90,
  };
}
